import { z } from 'zod';

/**
 * OuvidorIA — canal de dúvidas dos responsáveis no TaskAll Responsáveis, respondido por IA com
 * base no que a direção cadastra aqui. As duas coleções ficam no Firestore do projeto
 * taskall-responsaveis (contrato definido em functions/ouvidoria.js daquele repositório):
 *   ouvidoriaRequests      — perguntas gravadas pela function askOuvidoria; a escola responde aqui.
 *   ouvidoriaKnowledgeBase — base de conhecimento escrita pelo Admin Educ ({ schoolInep, title, content, category, active }).
 */
export const OUVIDORIA_REQUESTS_COLLECTION = 'ouvidoriaRequests';
export const OUVIDORIA_KNOWLEDGE_COLLECTION = 'ouvidoriaKnowledgeBase';
/** Limite de texto da base que a function envia ao modelo (MAX_KNOWLEDGE_CHARS). */
export const OUVIDORIA_KNOWLEDGE_MAX_CHARS = 60_000;
export const OUVIDORIA_KNOWLEDGE_MAX_ITEMS = 100;

export const OuvidoriaCategorySchema = z.enum([
  'calendario',
  'aulas',
  'avaliacoes',
  'secretaria',
  'financeiro',
  'transporte',
  'alimentacao',
  'eventos',
  'outros',
]);
export type OuvidoriaCategory = z.infer<typeof OuvidoriaCategorySchema>;

export const OUVIDORIA_CATEGORY_LABELS: Record<OuvidoriaCategory, string> = {
  calendario: 'Calendário',
  aulas: 'Aulas',
  avaliacoes: 'Avaliações',
  secretaria: 'Secretaria',
  financeiro: 'Financeiro',
  transporte: 'Transporte',
  alimentacao: 'Alimentação',
  eventos: 'Eventos',
  outros: 'Outros',
};

export const OuvidoriaStatusSchema = z.enum(['aguardando_escola', 'respondida', 'resolvida']);
export type OuvidoriaStatus = z.infer<typeof OuvidoriaStatusSchema>;

export const OUVIDORIA_STATUS_LABELS: Record<OuvidoriaStatus, string> = {
  aguardando_escola: 'Aguardando a escola',
  respondida: 'Respondida pela IA',
  resolvida: 'Resolvida',
};

// ------------------------------------------------------------------ solicitações
export const OuvidoriaRequestSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  guardianName: z.string(),
  /** Alunos desta escola vinculados ao responsável (sem CPF nem ids de outros projetos). */
  students: z.array(z.object({ name: z.string(), grade: z.string().nullable() })),
  question: z.string(),
  answer: z.string(),
  category: z.string(),
  answeredFromKnowledgeBase: z.boolean(),
  status: OuvidoriaStatusSchema,
  schoolReply: z.string().nullable(),
  repliedByName: z.string().nullable(),
  repliedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type OuvidoriaRequest = z.infer<typeof OuvidoriaRequestSchema>;

export const ListOuvidoriaRequestsResponseSchema = z.object({
  items: z.array(OuvidoriaRequestSchema),
  counts: z.record(OuvidoriaStatusSchema, z.number().int().nonnegative()),
});
export type ListOuvidoriaRequestsResponse = z.infer<typeof ListOuvidoriaRequestsResponseSchema>;

/** Resposta da escola (aparece para o responsável na OuvidorIA) e/ou mudança de situação. */
export const UpdateOuvidoriaRequestSchema = z
  .object({
    reply: z.string().trim().min(2, 'Escreva a resposta').max(2000, 'Máximo de 2.000 caracteres').optional(),
    status: z.enum(['resolvida', 'aguardando_escola']).optional(),
  })
  .refine((v) => v.reply !== undefined || v.status !== undefined, { message: 'Informe a resposta ou a situação' });
export type UpdateOuvidoriaRequestInput = z.infer<typeof UpdateOuvidoriaRequestSchema>;

// ------------------------------------------------------------------ base de conhecimento
export const KnowledgeBaseItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  category: OuvidoriaCategorySchema,
  active: z.boolean(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type KnowledgeBaseItem = z.infer<typeof KnowledgeBaseItemSchema>;

export const CreateKnowledgeBaseItemSchema = z.object({
  title: z.string().trim().min(3, 'Mínimo de 3 caracteres').max(120, 'Máximo de 120 caracteres'),
  content: z.string().trim().min(10, 'Descreva com pelo menos 10 caracteres').max(8000, 'Máximo de 8.000 caracteres por item'),
  category: OuvidoriaCategorySchema.default('outros'),
  active: z.boolean().default(true),
});
export type CreateKnowledgeBaseItemInput = z.input<typeof CreateKnowledgeBaseItemSchema>;
export type CreateKnowledgeBaseItemData = z.output<typeof CreateKnowledgeBaseItemSchema>;

export const UpdateKnowledgeBaseItemSchema = z
  .object({
    title: CreateKnowledgeBaseItemSchema.shape.title,
    content: CreateKnowledgeBaseItemSchema.shape.content,
    category: OuvidoriaCategorySchema,
    active: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nada para atualizar' });
export type UpdateKnowledgeBaseItemInput = z.infer<typeof UpdateKnowledgeBaseItemSchema>;

/** Código INEP de 8 dígitos (formato usado pelo TaskAll Responsáveis) a partir de XXXX-XXXX. */
export function inepDigits(schoolCode: string): string {
  return schoolCode.replace(/\D/g, '');
}
